import React, { useEffect, useState } from "react";
import api from "../api";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}>{children}</div>;
}

export default function ClientCompanies() {
  const { toast } = useToast();
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [newCo, setNewCo] = useState("");
  const [userFor, setUserFor] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", first_name: "", last_name: "", client_role: "client_user" });

  const load = () => {
    api.get("/admin/client-companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/admin/client-users").then((r) => setUsers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const createCompany = async () => {
    if (!newCo.trim()) return;
    try { await api.post("/admin/client-companies", { name: newCo }); setNewCo(""); toast({ title: "Client company created" }); load(); }
    catch (e) { toast({ title: "Failed", description: e.response?.data?.detail || "", variant: "destructive" }); }
  };

  const createUser = async () => {
    try {
      await api.post("/admin/client-users", { ...form, client_company_id: userFor.id });
      toast({ title: "Client user created", description: `${form.email} can now log in to the client portal.` });
      setUserFor(null); setForm({ email: "", password: "", first_name: "", last_name: "", client_role: "client_user" }); load();
    } catch (e) { toast({ title: "Failed", description: e.response?.data?.detail || "", variant: "destructive" }); }
  };

  return (
    <div className="space-y-4" data-testid="client-companies">
      <Card>
        <h2 className="text-base font-bold text-slate-900">Client Companies</h2>
        <p className="text-sm text-slate-500">Create client companies and client portal logins. Client users only ever see their own company's data.</p>
        <div className="mt-3 flex gap-2">
          <Input value={newCo} onChange={(e) => setNewCo(e.target.value)} placeholder="New company name" data-testid="new-company-name" />
          <Button onClick={createCompany} className="btn-primary-gradient border-0 text-white" data-testid="create-company-btn">Add company</Button>
        </div>
      </Card>

      {companies.map((c) => (
        <Card key={c.id} data-testid={`company-${c.id}`}>
          <div className="flex items-start justify-between">
            <div>
              <div className="font-bold text-slate-900">{c.name}</div>
              <div className="text-sm text-slate-500">{c.user_count} users · {c.program_count} programs</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setUserFor(c)} data-testid={`add-client-user-${c.id}`}>Add user</Button>
          </div>
          <div className="mt-3 space-y-1">
            {users.filter((u) => u.client_company_id === c.id).map((u) => (
              <div key={u.user_id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm">
                <span className="text-slate-700">{u.name} <span className="text-slate-400">· {u.email}</span></span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{u.client_role}</span>
              </div>
            ))}
          </div>
        </Card>
      ))}

      <Dialog open={!!userFor} onOpenChange={(o) => !o && setUserFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add client user — {userFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="cu-first" /></div>
              <div className="space-y-1.5"><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="cu-last" /></div>
            </div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cu-email" /></div>
            <div className="space-y-1.5"><Label>Temporary password</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="cu-password" /></div>
            <div className="space-y-1.5"><Label>Role</Label>
              <select className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm" value={form.client_role} onChange={(e) => setForm({ ...form, client_role: e.target.value })} data-testid="cu-role">
                <option value="client_user">Client User</option>
                <option value="client_admin">Client Admin</option>
              </select>
            </div>
          </div>
          <DialogFooter><Button onClick={createUser} disabled={!form.email || !form.password} className="btn-primary-gradient border-0 text-white" data-testid="create-client-user-btn">Create login</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
